import { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNotificationContext } from '@context/NotificationsContext';
import { useDesktop } from '@utils/hooks/useDesktop';
import { NOTIFICATION_NAMES_MAP } from '@utils/constants';
import { useCommerceFromStore } from '@store/actions/commerce';
import type { PlickNotification } from '@interfaces/general';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import * as Sentry from '@sentry/react';

const DEFAULT_COOK_TIME = 25;
const QUICK_PICKS = [15, 20, 25, 30, 45, 60] as const;

const LegacyCard = ({
  title,
  subtitle,
  description,
  body,
  icon,
  accentColor,
  onAccept,
  onReject,
  acceptLabel,
  rejectLabel,
  autoCloseTimer,
}: PlickNotification) => {
  const isDesktop = useDesktop();
  const timeOutBar = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (timeOutBar.current) timeOutBar.current.style.width = '0%';
    }, 100);
  }, []);

  return (
    <div className="rounded-lg bg-color-white px-3 py-3" style={{ boxShadow: 'rgba(0,0,0,0.25) 0px 0px 10px 5px' }}>
      <div className={`flex ${isDesktop ? 'flex-row' : 'flex-col'}`}>
        <div className="flex flex-1 items-center">
          <div
            className="w-[41px] min-w-[41px] h-10 rounded-[50%] flex items-center justify-center"
            style={{ backgroundColor: accentColor }}
          >
            <Icon icon={icon} color="#ffffff" width="28px" height="28px" />
          </div>
          <div className="ml-3">
            <p className="text-lg font-semibold">
              {title} {subtitle ? <b>{subtitle}</b> : null}
            </p>
            <p>{description}</p>
            {body ?? null}
          </div>
        </div>
        {(onAccept || onReject) && (
          <div
            className="flex flex-col justify-center"
            style={{
              borderLeft: isDesktop ? '1px solid #C0C0C0' : 'none',
              width: isDesktop ? '135px' : '100%',
              marginTop: isDesktop ? '0px' : '12px',
              paddingLeft: isDesktop ? '12px' : '0px',
            }}
          >
            {onAccept && (
              <button
                className="font-bold text-color-white py-2 rounded-md flex items-center justify-center mb-1"
                style={{ backgroundColor: accentColor }}
                onClick={() => onAccept()}
              >
                {acceptLabel ? acceptLabel.toUpperCase() : 'ACEPTAR'}
                <Icon className="ml-1" icon="mingcute:check-fill" color="#ffffff" />
              </button>
            )}
            {onReject && (
              <button
                className="font-bold py-2 flex items-center justify-center"
                onClick={() => onReject()}
                style={{ color: '#FF3939', marginTop: isDesktop ? '0' : '4px' }}
              >
                {rejectLabel ? rejectLabel.toUpperCase() : 'RECHAZAR'}
                <Icon className="ml-1" icon="ep:close-bold" color="#FF3939" />
              </button>
            )}
          </div>
        )}
      </div>
      <div
        ref={autoCloseTimer ? timeOutBar : null}
        className="h-2 rounded-md"
        style={{
          marginTop: '8px',
          backgroundColor: accentColor,
          width: '100%',
          transition: autoCloseTimer ? `width ${autoCloseTimer}ms ease-in-out` : 'none',
        }}
      />
    </div>
  );
};

// Card dedicada para "pedido programado creado": mismo lenguaje visual que el widget de pedido
// entrante (redondeada, sombra, ícono en círculo), pero acento ámbar y SIN cook-time ni
// aceptar/rechazar-cocina. Evita caer en LegacyCard (que descuadraba el label largo).
const ScheduledCard = ({
  title,
  subtitle,
  description,
  body,
  icon,
  accentColor,
  onAccept,
  onReject,
  acceptLabel,
  rejectLabel,
  autoCloseTimer,
}: PlickNotification) => {
  const timerBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!timerBarRef.current) return;
    timerBarRef.current.style.transition = 'none';
    timerBarRef.current.style.width = '100%';
    const id = setTimeout(() => {
      if (!timerBarRef.current) return;
      // NotificationsContext.addNotification remueve el toast a `autoCloseTimer - 250`ms (margen
      // de seguridad para el timer de JS). La barra debe animar a esa misma duración EFECTIVA —
      // si anima el `autoCloseTimer` completo, se corta con un remanente visible ~250ms antes de
      // llegar a 0%. Ajuste aislado a esta card (no toca el offset compartido de
      // NotificationsContext, usado por TODAS las notificaciones, incluida la de pedido entrante).
      const duration = Math.max((autoCloseTimer ?? 15000) - 250, 0);
      timerBarRef.current.style.transition = `width ${duration}ms linear`;
      timerBarRef.current.style.width = '0%';
    }, 100);
    return () => clearTimeout(id);
  }, [autoCloseTimer]);

  return (
    <div
      className="rounded-[12px] overflow-hidden"
      style={{
        boxShadow: '0 16px 40px rgba(37,55,70,.22)',
        animation: 'plick-toast-in 280ms cubic-bezier(.2,.8,.2,1)',
      }}
    >
      <div className="flex items-center gap-3" style={{ padding: '12px 14px 10px', backgroundColor: '#ffffff' }}>
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(245,158,11,0.15)' }}
        >
          <Icon icon={icon} color={accentColor} width={28} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold leading-tight" style={{ fontSize: 14, color: '#253746' }}>
            {title} <strong>{subtitle ?? ''}</strong>
          </p>
          <p style={{ fontSize: 13, color: '#6B6B6B', marginTop: 2 }}>{description}</p>
          {body ? <div style={{ fontSize: 13, marginTop: 2 }}>{body}</div> : null}
        </div>
      </div>
      <div className="flex items-center gap-2" style={{ padding: '10px 14px', backgroundColor: '#ffffff' }}>
        {onReject && (
          <button
            onClick={onReject}
            className="font-bold uppercase"
            style={{
              background: 'transparent',
              border: 0,
              color: '#6B6B6B',
              fontSize: 12,
              letterSpacing: '.03em',
              cursor: 'pointer',
              padding: '6px 8px',
              fontFamily: 'inherit',
            }}
          >
            {rejectLabel ?? 'Ok'}
          </button>
        )}
        {onAccept && (
          <button
            onClick={() => onAccept()}
            className="flex items-center justify-center gap-1.5 rounded-md font-bold"
            style={{
              marginLeft: 'auto',
              backgroundColor: accentColor,
              color: '#ffffff',
              fontSize: 13,
              padding: '8px 16px',
              border: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
          >
            {acceptLabel ?? 'Ver'}
            <Icon icon="mingcute:arrow-right-line" width={15} color="#ffffff" />
          </button>
        )}
      </div>
      <div style={{ height: 4, background: '#F3E3C6', position: 'relative', overflow: 'hidden' }}>
        <div ref={timerBarRef} style={{ height: '100%', backgroundColor: accentColor, width: '100%' }} />
      </div>
    </div>
  );
};

const SCHEDULED_NOTIF_SUFFIX = '_scheduled_created';

export const NotificationCenterV2 = () => {
  const isDesktop = useDesktop();
  const { notifications } = useNotificationContext();
  const commerceStore = useCommerceFromStore();
  const defaultCookTime = commerceStore?.default_cook_time ?? null;
  const cookTimeEnabled = Boolean(defaultCookTime && defaultCookTime > 0);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [cookTime, setCookTime] = useState(() =>
    defaultCookTime && defaultCookTime > 0 ? defaultCookTime : DEFAULT_COOK_TIME,
  );
  const [cookTimeEditing, setPrepEditing] = useState(false);
  const [cookTimeInputValue, setPrepInputValue] = useState('');
  const timerBarRef = useRef<HTMLDivElement>(null);

  const incomingOrders = notifications.filter((n) => n.id.endsWith(NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key));
  const scheduledNotifications = notifications.filter((n) => n.id.endsWith(SCHEDULED_NOTIF_SUFFIX));
  const otherNotifications = notifications.filter(
    (n) => !n.id.endsWith(NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key) && !n.id.endsWith(SCHEDULED_NOTIF_SUFFIX),
  );

  useEffect(() => {
    if (incomingOrders.length === 0) {
      setCurrentIndex(0);
    } else if (currentIndex >= incomingOrders.length) {
      setCurrentIndex(incomingOrders.length - 1);
    }
  }, [incomingOrders.length, currentIndex]);

  const current = incomingOrders[currentIndex];
  // El cook time solo se promete a clientes remotos que reciben la notificación ACCEPTED:
  // 'pasar a recoger'. Mesa (cliente presente, sin teléfono) y domicilio (ETA total ≠ cocina) se excluyen.
  const showCookTime = cookTimeEnabled && current?.metodo_entrega === 'takeAway';

  const { trackEvent } = useTracking();

  useEffect(() => {
    setCookTime(defaultCookTime && defaultCookTime > 0 ? defaultCookTime : DEFAULT_COOK_TIME);
    setPrepEditing(false);
  }, [current?.id, defaultCookTime]);

  // Aplica una nueva ETA y, si difiere del default del comercio, instrumenta el ajuste.
  // GA4 (`eta_edited`) mide cuántas veces el comercio cambia el tiempo comprometido respecto
  // a su default; el breadcrumb deja rastro en Sentry para correlacionar con fallas posteriores.
  const applyCookTime = useCallback(
    (val: number) => {
      setCookTime(val);
      const effectiveDefault = defaultCookTime && defaultCookTime > 0 ? defaultCookTime : DEFAULT_COOK_TIME;
      if (val !== effectiveDefault) {
        Sentry.addBreadcrumb({
          category: 'cook_time',
          message: 'eta_edited',
          level: 'info',
          data: { value_minutes: val, default_minutes: effectiveDefault },
        });
        trackEvent(TRACK_EVENT_NAMES.EtaEdited, {
          value_minutes: val,
          default_minutes: effectiveDefault,
        });
      }
    },
    [defaultCookTime, trackEvent],
  );

  const commitCookTimeEdit = useCallback(() => {
    const val = parseInt(cookTimeInputValue, 10);
    if (!isNaN(val) && val > 0) applyCookTime(val);
    setPrepEditing(false);
  }, [cookTimeInputValue, applyCookTime]);

  useEffect(() => {
    if (!timerBarRef.current) return;
    timerBarRef.current.style.transition = 'none';
    timerBarRef.current.style.width = '100%';
    const id = setTimeout(() => {
      if (!timerBarRef.current) return;
      const duration = current?.autoCloseTimer ?? 30000;
      timerBarRef.current.style.transition = `width ${duration}ms linear`;
      timerBarRef.current.style.width = '0%';
    }, 100);
    return () => clearTimeout(id);
  }, [current?.id]);

  const total = incomingOrders.length;

  if (notifications.length === 0) return null;

  return (
    <div
      className={`fixed bottom-0 right-0 z-[99] flex flex-col gap-3 ${
        isDesktop ? 'w-[400px] mr-5 mb-5' : 'w-full px-3 pb-[70px]'
      }`}
      style={{ maxHeight: 'calc(100vh - 20px)', overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' }}
    >
      {/* Legacy notifications (RIDER_ARRIVED, ORDER_DELIVERED, etc.) */}
      {otherNotifications.map((n) => (
        <LegacyCard key={n.id} {...n} />
      ))}

      {/* Pedido programado creado (notificación silenciosa, acento ámbar) */}
      {scheduledNotifications.map((n) => (
        <ScheduledCard key={n.id} {...n} />
      ))}

      {/* Incoming-order widget */}
      {current && (
        <div
          className="rounded-[12px] overflow-hidden"
          style={{
            boxShadow: '0 16px 40px rgba(37,55,70,.22)',
            animation: 'plick-toast-in 280ms cubic-bezier(.2,.8,.2,1)',
          }}
        >
          {/* Red header bar — only when multiple orders are queued */}
          {total > 1 && (
            <div
              className="flex items-center justify-between px-3 py-[5px] text-white"
              style={{ backgroundColor: '#ff3939' }}
            >
              <span className="font-bold uppercase tracking-[0.04em] flex items-center gap-2" style={{ fontSize: 12 }}>
                <span
                  className="w-1.5 h-1.5 rounded-full bg-white flex-shrink-0"
                  style={{ animation: 'plick-nc-pulse 1.4s ease-out infinite' }}
                />
                Pedido {currentIndex + 1} de {total} nuevos
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                  disabled={currentIndex === 0}
                  className="w-5 h-5 flex items-center justify-center rounded disabled:opacity-40"
                  style={{ backgroundColor: currentIndex > 0 ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)' }}
                  aria-label="Anterior pedido"
                >
                  <Icon icon="bxs:chevron-left" width={14} color="#ffffff" />
                </button>
                <button
                  onClick={() => setCurrentIndex((i) => Math.min(total - 1, i + 1))}
                  disabled={currentIndex === total - 1}
                  className="w-5 h-5 flex items-center justify-center rounded disabled:opacity-40"
                  style={{
                    backgroundColor: currentIndex < total - 1 ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)',
                  }}
                  aria-label="Siguiente pedido"
                >
                  <Icon icon="bxs:chevron-right" width={14} color="#ffffff" />
                </button>
              </div>
            </div>
          )}

          {/* Order summary */}
          <div className="flex items-center gap-3" style={{ padding: '12px 14px 10px', backgroundColor: '#ffffff' }}>
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'rgba(87,189,227,0.2)' }}
            >
              <Icon icon={current.icon} color={current.accentColor} width={28} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold leading-tight" style={{ fontSize: 14, color: '#253746' }}>
                {current.title} <strong>{current.subtitle ?? ''}</strong>
              </p>
              <p style={{ fontSize: 13, color: '#6B6B6B', marginTop: 2 }}>{current.description}</p>
              {current.body ? <div style={{ fontSize: 13, marginTop: 2 }}>{current.body}</div> : null}
            </div>
          </div>

          {/* Prep time section */}
          {showCookTime && (
            <div
              data-testid="cook-time-section"
              style={{ padding: '0 14px 12px', backgroundColor: '#ffffff', borderTop: '1px solid #E8EEF2' }}
            >
              <div className="flex items-center gap-2" style={{ paddingTop: 10 }}>
                <Icon icon="mingcute:time-line" color="#6B6B6B" width={16} />
                <span style={{ fontSize: 13, color: '#6B6B6B', fontWeight: 500 }}>Listo en</span>
                {cookTimeEditing ? (
                  <input
                    autoFocus
                    type="number"
                    value={cookTimeInputValue}
                    onChange={(e) => setPrepInputValue(e.target.value)}
                    onBlur={commitCookTimeEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitCookTimeEdit();
                      if (e.key === 'Escape') setPrepEditing(false);
                    }}
                    style={{
                      width: 52,
                      border: '1px solid #1fc49a',
                      borderRadius: 4,
                      padding: '2px 6px',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#1fc49a',
                      outline: 'none',
                      textAlign: 'center',
                    }}
                  />
                ) : (
                  <button
                    onClick={() => {
                      setPrepInputValue(String(cookTime));
                      setPrepEditing(true);
                    }}
                    style={{
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                      fontSize: 16,
                      fontWeight: 700,
                      color: '#1fc49a',
                      padding: '0 4px',
                      fontFamily: 'inherit',
                    }}
                  >
                    {cookTime} min
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap" style={{ marginTop: 8 }}>
                {QUICK_PICKS.map((t) => (
                  <button
                    key={t}
                    onClick={() => {
                      applyCookTime(t);
                      setPrepEditing(false);
                    }}
                    style={{
                      border: `1px solid ${cookTime === t ? '#1fc49a' : '#D0D7DE'}`,
                      borderRadius: 6,
                      padding: '3px 8px',
                      fontSize: 12,
                      fontWeight: cookTime === t ? 700 : 400,
                      color: cookTime === t ? '#1fc49a' : '#6B6B6B',
                      backgroundColor: cookTime === t ? 'rgba(31,196,154,.08)' : 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {t} min
                  </button>
                ))}
              </div>
              {/* El comensal ve este minutaje convertido a hora absoluta ("Listo aprox. a las 7:45 PM") en la
                  pantalla de "pedido enviado" del menú público, con reloj corriendo: al vencerse, el badge se degrada
                  y luego le ofrece llamar al restaurante. El copy anterior ("Tiempo comprometido al cliente") era
                  cierto pero abstracto — no delataba que el número se publica ni que se lee como hora. */}
              <p style={{ fontSize: 11, color: '#9BA5AE', marginTop: 6 }}>El cliente verá a qué hora estará listo</p>
            </div>
          )}

          {/* Actions row */}
          <div className="flex items-center gap-2" style={{ padding: '10px 14px', backgroundColor: '#ffffff' }}>
            {current.onReject && (
              <button
                onClick={current.onReject}
                className="flex items-center gap-1 font-bold uppercase"
                style={{
                  background: 'transparent',
                  border: 0,
                  color: '#FF3939',
                  fontSize: 12,
                  letterSpacing: '.03em',
                  cursor: 'pointer',
                  padding: '6px 8px',
                  fontFamily: 'inherit',
                }}
              >
                {(current.rejectLabel ?? 'Ignorar').toUpperCase()}
                <Icon icon="ep:close-bold" color="#FF3939" width={12} />
              </button>
            )}
            {current.onAccept && (
              <button
                onClick={() => current.onAccept?.(showCookTime ? cookTime : undefined)}
                className="flex items-center justify-center gap-1.5 rounded-md font-bold"
                style={{
                  marginLeft: 'auto',
                  backgroundColor: '#1fc49a',
                  color: '#ffffff',
                  fontSize: 14,
                  padding: '8px 16px',
                  border: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {(current.acceptLabel ?? 'ACEPTAR').toUpperCase()}
                <Icon icon="mingcute:check-fill" width={15} color="#ffffff" />
              </button>
            )}
          </div>

          {/* Timer bar */}
          <div style={{ height: 4, background: '#E8EEF2', position: 'relative', overflow: 'hidden' }}>
            <div ref={timerBarRef} style={{ height: '100%', backgroundColor: current.accentColor, width: '100%' }} />
          </div>
        </div>
      )}
      <style>{`
        @keyframes plick-toast-in {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
        @keyframes plick-nc-pulse {
          0%  { transform: scale(.85); opacity: .6; }
          70% { transform: scale(1.4); opacity: 0; }
          100%{ transform: scale(1.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
};
